import type { BranchKey } from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'
import { withSuspense } from '@festina-life/ui/libs'
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router'

import { useNavigationParams } from '../system/useNavigationParams'
import { isEqualBranch } from '../uiModels/branches/helpers'
import { useCurrentBranchConciseChangesets } from '../uiModels/changesets/useCurrentBranchConciseChangesets'
import { Provider } from './changesetStateContext'
import { useUntilChangesetKeyValue } from './useUntilChangesetKeyValue'

const ChangesetStateProviderInternal = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate()
  const { pathname, hash } = useLocation()
  const untilChangesetKeyValue = useUntilChangesetKeyValue()

  const currentBranchChangesets = useCurrentBranchConciseChangesets({
    shouldIncludeBaseConciseChangesets: true,
  })
  const currentBranchChangesetKeys = useMemo(
    () =>
      currentBranchChangesets
        .map((changeset) => changeset.changesetKey)
        .filter(notEmpty),
    [currentBranchChangesets],
  )

  const { branch: currentBranch, server } = useNavigationParams()
  const previousBranchKey = useRef<Optional<BranchKey>>(
    currentBranch
      ? {
          name: currentBranch,
          server,
        }
      : undefined,
  )
  const [disabledChangesetKeys, setDisabledChangesetKeys] = useState<string[]>(
    [],
  )

  const clearSearchParams = useCallback(
    () => navigate(`${pathname}${hash}`),
    [navigate, pathname, hash],
  )

  const disableKeys = async (changesetKeys: string[]) => {
    await clearSearchParams()
    setDisabledChangesetKeys(
      Array.from(new Set([...disabledChangesetKeys, ...changesetKeys])),
    )
  }

  const enableKeys = async (changesetKeys: string[]) => {
    await clearSearchParams()
    setDisabledChangesetKeys(
      disabledChangesetKeys.filter((key) => !changesetKeys.includes(key)),
    )
  }

  const reset = useCallback(async () => {
    await clearSearchParams()
    setDisabledChangesetKeys([])
  }, [clearSearchParams])

  // Creates a useEffectEvent since we don't want the effect to be dependent on the disabledChangesetKeys value
  const onUntilChangesetKeyChanged = useEffectEvent(() => {
    if (untilChangesetKeyValue) {
      const indexOfUntilChangesetKey = currentBranchChangesetKeys.indexOf(
        untilChangesetKeyValue,
      )

      if (indexOfUntilChangesetKey !== -1) {
        const nextDisabledKeys = currentBranchChangesetKeys.slice(
          0,
          indexOfUntilChangesetKey,
        )
        const isAlreadyDisabled =
          disabledChangesetKeys.length === nextDisabledKeys.length &&
          nextDisabledKeys.every((key) => disabledChangesetKeys.includes(key))

        if (!isAlreadyDisabled) {
          setDisabledChangesetKeys(nextDisabledKeys)
        }
      }
    }

    const currentBranchKey = currentBranch
      ? { name: currentBranch, server }
      : undefined
    previousBranchKey.current = currentBranchKey
  })

  useEffect(
    function updateDisabledChangesetKeysOnUntilChangesetKeyChange() {
      onUntilChangesetKeyChanged()
    },
    [untilChangesetKeyValue],
  )

  useEffect(
    function resetDisabledChangesetKeysOnBranchChange() {
      const currentBranchKey = currentBranch
        ? { name: currentBranch, server }
        : undefined
      if (
        currentBranchKey &&
        !isEqualBranch(currentBranchKey, previousBranchKey.current)
      ) {
        setDisabledChangesetKeys([])
      }

      return () => {
        previousBranchKey.current = currentBranchKey
      }
    },
    [currentBranch, server, previousBranchKey],
  )

  const areAllChangesetsDisabled =
    disabledChangesetKeys.length === currentBranchChangesetKeys.length
  const onDisableAll = useCallback(async () => {
    await clearSearchParams()
    setDisabledChangesetKeys(currentBranchChangesetKeys)
  }, [clearSearchParams, currentBranchChangesetKeys])

  return (
    <Provider
      value={{
        disabledChangesetKeys,
        onDisable: disableKeys,
        onEnable: enableKeys,
        onReset: reset,
        areAllChangesetsDisabled,
        onDisableAll,
      }}
    >
      {children}
    </Provider>
  )
}

export const ChangesetStateProvider = withSuspense(
  ChangesetStateProviderInternal,
)
